import { ReactElement } from 'react'

import {
  Box,
  Breadcrumbs,
  BreadcrumbsProps,
  Link,
  Typography,
} from '@mui/material'
import last from 'lodash/last'
import { Link as RouterLink } from 'react-router-dom'

export interface MBreadcrumbsProps extends BreadcrumbsProps {
  links: TLink[]
  activeLast?: boolean
}

type TLink = {
  href?: string
  name: string
  icon?: ReactElement
}

const Separator = (
  <Box
    component="span"
    sx={{
      width: 4,
      height: 4,
      borderRadius: '50%',
      bgcolor: 'text.disabled',
    }}
  />
)

const LinkItem = ({ link }: { link: TLink }) => {
  const { href, name, icon } = link
  return (
    <Link
      key={name}
      variant="body2"
      component={RouterLink}
      to={href || '#'}
      sx={{
        lineHeight: 2,
        display: 'flex',
        alignItems: 'center',
        color: 'text.primary',
        '& > div': { display: 'inherit' },
      }}
    >
      {icon && (
        <Box
          sx={{
            mr: 1,
            '& svg': { width: 20, height: 20 },
          }}
        >
          {icon}
        </Box>
      )}
      {name}
    </Link>
  )
}

const MBreadcrumbs = ({
  links,
  activeLast = false,
  expandText = 'Show path',
  itemsAfterCollapse = 1,
  itemsBeforeCollapse = 1,
  maxItems = 8,
}: MBreadcrumbsProps) => {
  const currentLink = last(links)?.name

  const listDefault = links.map((link) => (
    <LinkItem key={link.name} link={link} />
  ))

  const listActiveLast = links.map((link) => (
    <div key={link.name}>
      {link.name !== currentLink ? (
        <LinkItem link={link} />
      ) : (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 260,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            color: 'text.disabled',
            textOverflow: 'ellipsis',
          }}
        >
          {currentLink}
        </Typography>
      )}
    </div>
  ))

  return (
    <Breadcrumbs
      separator={Separator}
      expandText={expandText}
      itemsAfterCollapse={itemsAfterCollapse}
      itemsBeforeCollapse={itemsBeforeCollapse}
      maxItems={maxItems}
    >
      {activeLast ? listDefault : listActiveLast}
    </Breadcrumbs>
  )
}

export default MBreadcrumbs
